import type { Route } from 'components/dynamic-page/route-matchers/types';
import type { ReadonlyDeep } from 'type-fest';
import type { CountryCode, CountryCodeList } from 'types/countries';
import type { SiteLanguage } from 'constants/locale';
import type { Slide } from 'components/autoplay-slides';
import { HtmlTransClassNames } from 'components/html-trans';
import { joinExpCaPaths, joinExpUsPaths } from 'types/locales';
import { ThemeConfig, ThemeNames } from './theme';

import type { NextRequest, NextResponse } from 'next/server';
import type { Namespaces } from 'types/locales';
import type { TiltedCardsProps } from 'components/tilted-cards';

//#region Tenant IDs
export const EXP_COMMERCIAL_TENANT_ID = 'expCommercial' as const;
export const EXP_IN_SERVICE_TENANT_ID = 'expInService' as const;
export const EXP_REALTY_CA_TENANT_ID = 'expRealtyCa' as const;
export const EXP_REALTY_US_TENANT_ID = 'expRealtyUs' as const;
export const ZOOCASA_TENANT_ID = 'zoocasa' as const;
//#endregion

export type EXP_COMMERCIAL_TENANT_ID_TYPE = typeof EXP_COMMERCIAL_TENANT_ID;
export type EXP_IN_SERVICE_TENANT_ID_TYPE = typeof EXP_IN_SERVICE_TENANT_ID;
export type EXP_REALTY_CA_TENANT_ID_TYPE = typeof EXP_REALTY_CA_TENANT_ID;
export type EXP_REALTY_US_TENANT_ID_TYPE = typeof EXP_REALTY_US_TENANT_ID;
export type ZOOCASA_TENANT_ID_TYPE = typeof ZOOCASA_TENANT_ID;

export type TenantId =
  | ZOOCASA_TENANT_ID_TYPE
  | EXP_IN_SERVICE_TENANT_ID_TYPE
  | EXP_REALTY_CA_TENANT_ID_TYPE
  | EXP_REALTY_US_TENANT_ID_TYPE
  | EXP_COMMERCIAL_TENANT_ID_TYPE;

export const TenantIds = {
  ZOOCASA: ZOOCASA_TENANT_ID,
  EXP_IN_SERVICE: EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA: EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US: EXP_REALTY_US_TENANT_ID,
  EXP_COMMERCIAL: EXP_COMMERCIAL_TENANT_ID,
} as const;

export type FooterColItem = {
  label: string;
  link?: string;
  target?: string;
  isAnchor?: boolean;
};

export type FooterImageData = {
  src: string;
  alt: string;
};

export type ExpFooterComponentProps = {
  config: FooterConfig;
};

export type HeaderConfig = ReadonlyDeep<{
  mainButtonCopy: string;
  authButtonCopy: string;
  hasJoinExpPage: boolean;
  authMenuItems: {
    label: string;
    link?: string;
    id: string;
    signOut?: boolean;
  }[];
  menuItems: {
    label: string;
    link?: string;
    id: string;
    newTab?: boolean;
  }[];
}>;

export type ContentCard = {
  title: string;
  image: string;
  titleColor: string;
  backgroundColor: string;
  content: string;
  link: string;
  desktopTransform?: string;
  tabletTransform?: string;
  translateYDesktop?: string;
  translateYTablet?: string;
  rounded?: boolean;
  altText?: string;
  buttonText?: string;
};

export type TestimonialProps = {
  text: string;
  image: string;
  alt: string;
  name: string;
  company: string;
  index: number;
};

export type InternalLinks = {
  title: string;
  links: {
    label: string;
    link: string;
  }[];
};

export type HomeConfig = ReadonlyDeep<{
  heroVideo?: string;
  heroImage?: string;
  heroImageAlt?: string;
  joinLink?: string;
  testimonialBlock?: {
    backgroundImage: string;
    heading: string;
    testimonials: TestimonialProps[];
  };
  joinUsSection?: {
    link: string;
    title: string;
    title2: string;
    description: string;
    imageUrl: string;
    buttonText: string;
  };
  contentCards?: {
    sectionTitleParts: string[];
    cards: ContentCard[];
  };
  carouselImages?: string[];
  internalLinks?: InternalLinks[];
  autoplaySlides?: {
    title: string;
    description: string;
    linkText: string;
    linkPath: string;
    slides: Slide[];
  };
  tiltedCards?: TiltedCardsProps;
}>;

export type OrganizationFooter = {
  phoneNumber: (route?: Route) => string | null;
  email: (route?: Route) => string | null;
  address: (siteLocation?: CountryCodeList) => string | null;
  twitterUrl: string;
  facebookUrl: string;
  instagramUrl: string;
  linkedinUrl: string;
  youtubeUrl?: string;
  joinSectionUrl?: string;
};

export type FooterConfig = ReadonlyDeep<{
  footerColumns?: {
    column1?: FooterColItem[];
    column2?: FooterColItem[];
    column3?: FooterColItem[];
    bottom?: FooterColItem[][];
    joinUsLink?: string;
    footerImageData?: FooterImageData;
  };
  organization: OrganizationFooter;
  showAccessibilityLink?: boolean;
}>;

export type AgentSearchConfig = ReadonlyDeep<{
  headerImage: {
    mobileSrc: string;
    src: string;
    alt: string;
  };
  agentTypeName: string;
  featuredAgentDescriptor: string;
  pageTitle: string;
  showCountrySelect?: boolean;
}>;

export type ContactUsConfig = ReadonlyDeep<{
  socialLinks: {
    facebookUrl: string;
    linkedinUrl: string;
    youtubeUrl: string;
    instagramUrl: string;
  };
  title: string;
  description: string;
  notificationContent: string;
  bottomContent: {
    title: string;
    description: string;
  };
}>;

export type CommercialBenefitsCard = {
  title?: string;
  titleHtml?: string;
  description: string;
  type: 'card';
};

export type CommercialBenefitCardMedia = {
  url: string;
  type: 'image';
};

export type CommercialBenefits = ReadonlyDeep<{
  joinUsLink: string;
  benefits: (CommercialBenefitsCard | CommercialBenefitCardMedia)[];
}>;

export type CommercialMetricsConfig = ReadonlyDeep<{
  metrics: CommercialMetrics[];
  backgroundVideo: {
    src: string;
    type: string;
  };
  logo: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
}>;

export type CommercialMetrics = {
  value: string;
  label: string;
};

export type SearchOurProperties = ReadonlyDeep<{
  title: {
    __html: string;
  };
  linkText: string;
  linkPath: string;
  imageCarousel: string[];
}>;

export type ExpCommercialConfig = ReadonlyDeep<{
  quotes: {[authorId: string]: Quote };
  metrics: CommercialMetricsConfig;
  benefits: CommercialBenefits;
  searchOurProperties: SearchOurProperties;
  faq: FAQ;
}>;

const MY_LINK_MY_LEAD_FEATURE_NAME = 'MyLinkMyLead' as const;
const AGENT_SEARCH_FEATURE_NAME = 'AgentSearch' as const;
const ACCESSIBILITY_WIDGET_FEATURE_NAME = 'AccessibilityWidget' as const;
const TRUSTARC_FEATURE_NAME = 'TrustArc' as const;
export type Features = {
  [MY_LINK_MY_LEAD_FEATURE_NAME]?: boolean;
  [AGENT_SEARCH_FEATURE_NAME]?: boolean;
  /** Determines if the accessibility widget should be shown for a given tenant. Default is `false` */
  [ACCESSIBILITY_WIDGET_FEATURE_NAME]?: boolean;
  /** Determines if the TrustArc script should be included for a given tenant. Default is `false` */
  [TRUSTARC_FEATURE_NAME]?: {
    enabled: boolean;
    domain: string;
  };
};

export type Quote = ReadonlyDeep<{
  authorImage: string;
  authorImageAlt: string;
  authorTitle: string;
  authorName: string;
  quoteContent: string;
  highlightColor?: string;
  className?: HtmlTransClassNames;
}>;

export type FAQ = ReadonlyDeep<{
  title: string;
  content: FaqItem[];
}>;

export type FaqItem = ReadonlyDeep<{
  title: string;
  children: string;
  styleOverrides?: Record<string, any>;
  values?: Record<string, any>;
}>;

export type VideoCardData = {
  videoId: string;
  bottomText: string;
};

export type CardStackData = ReadonlyDeep<{
  title: string;
  content: CardStackItem[];
}>;

export type CardStackItem = ReadonlyDeep<{
  title: string;
  description: string;
  imageSrc: string;
  backgroundColor: string;
  textColor: string;
  imageAlt: string;
}>;

export type ProgramData = ReadonlyDeep<{
  image: string;
  imageAlt: string;
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  buttonClickEvent?: string;
}>;

export type PowerOfExpRegion = {
  id: string;
  title: joinExpCaPaths | joinExpUsPaths;
  imageSrc: joinExpCaPaths | joinExpUsPaths;
  url: joinExpCaPaths | joinExpUsPaths;
};

export type PowerOfExpData = ReadonlyDeep<{
  title: string;
  expExplainedHashtag: string;
  section1: {
    image: string;
    imageAlt: string;
    title: string;
    info: string;
    timing: string;
    button: string;
    signMeUpHref: string;
  };
  section2: {
    image: string;
    imageAlt: string;
    title: {
      mobile: string;
      desktop: string;
    };
    info: string;
    button: {
      mobile: string;
      desktop: string;
    };
  };
  presentation: {
    modal: {
      title: string;
      downloadButton: string;
      regions: PowerOfExpRegion[];
    };
  };
}>;

export type BuiltForThisLogo = {
  src: string;
  alt: string;
  width?: number | `${number}` | undefined;
  height?: number | `${number}` | undefined;
};

export type BuiltForThisVideo = {
  src: string;
  type: string;
};

export type BuildForThisData = {
  label: string;
  clickEvent: string;
  logo: Record<SiteLanguage, BuiltForThisLogo>;
  video: Record<SiteLanguage, BuiltForThisVideo>;
};

// Define the config type
export type JoinExpConfig = ReadonlyDeep<{
  joinPageContactPage: string;
  joinPageContactPath: string;
  learnMoreClickEvent: string;
  signMeUpClickEvent: string;
  getNowClickEvent: string;
  quotes: {[authorId: string]: Quote };
  videoCardData: VideoCardData[];
  cardStackData: CardStackData;
  programsData: ProgramData[];
  faq: FAQ;
  powerOfExpData: PowerOfExpData;
  builtForThis: BuildForThisData;
}>;

export type SEOConfig = {
  noFollowRoutes?: Route[]; // Routes to apply nofollow metadata to
};

export type FiltersConfig = {
  hideExpiredListings?: boolean; // Determines whether the filter bar will allow users to filter for expired listings
  /** This is used to set the country code for the filter bar. */
  countryCode?: CountryCode;
};

export type ContentHostSelector = CountryCodeList | 'ALL';
export type ContentHosts = Record<ContentHostSelector, string>;

type RouteHandler = (
  req: NextRequest,
) => NextResponse | undefined | Promise<NextResponse | undefined>;

type RouteMatcher = string | RegExp;

type RouteHandlerEntry = {
  match: RouteMatcher;
  handler: RouteHandler;
};

type TenantRouteConfig = {
  claimed: '*' | string[];
  excluded?: string[];
  handlers?: RouteHandlerEntry[];
};

export const TENANT_CONFIG_HOME_KEY = 'home' as const;
export const TENANT_CONFIG_HEADER_KEY = 'header' as const;
export const TENANT_CONFIG_FOOTER_KEY = 'footer' as const;
export const TENANT_CONFIG_AGENT_SEARCH_KEY = 'agentSearch' as const;
export const TENANT_CONFIG_SEO_KEY = 'seo' as const;
export const TENANT_CONFIG_FILTERS_KEY = 'filters' as const;
export const TENANT_CONFIG_JOIN_EXP_KEY = 'joinExp' as const;
export const TENANT_CONFIG_EXP_COMMERCIAL_KEY = 'commercial' as const;
export const TENANT_CONFIG_CONTACT_US_KEY = 'contactUs' as const;

export type TenantConfigHome = { [TENANT_CONFIG_HOME_KEY]: HomeConfig };
export type TenantConfigHeader = { [TENANT_CONFIG_HEADER_KEY]: HeaderConfig };
export type TenantConfigFooter = { [TENANT_CONFIG_FOOTER_KEY]: FooterConfig };
export type TenantConfigAgentSearch = { [TENANT_CONFIG_AGENT_SEARCH_KEY]: AgentSearchConfig };
export type TenantConfigSEO = { [TENANT_CONFIG_SEO_KEY]: SEOConfig };
export type TenantConfigFilters = { [TENANT_CONFIG_FILTERS_KEY]: FiltersConfig };
export type TenantConfigJoinExp = { [TENANT_CONFIG_JOIN_EXP_KEY]: JoinExpConfig };
export type TenantConfigExpCommercial = { [TENANT_CONFIG_EXP_COMMERCIAL_KEY]: ExpCommercialConfig };
export type TenantConfigContactUs = { [TENANT_CONFIG_CONTACT_US_KEY]: ContactUsConfig };

export const DEFAULT_GTM_CONTAINER_ID_KEY = 'defaultContainerId' as const;

export type TenantConfig =
  | TenantConfigHome
  | TenantConfigHeader
  | TenantConfigFooter
  | TenantConfigAgentSearch
  | TenantConfigSEO
  | TenantConfigFilters
  | TenantConfigJoinExp
  | TenantConfigExpCommercial
  | TenantConfigContactUs
  | object;

export type Tenant<
  I extends TenantId,
  T extends ThemeConfig<ThemeNames>,
  C extends TenantConfig = object,
  G extends string = typeof DEFAULT_GTM_CONTAINER_ID_KEY
> = ReadonlyDeep<{
  /**
   * The ID of the tenant.
   *
   * This is used to identify the tenant.
   *
   * @example
   * ```ts
   * const tenant = getTenantById(ZOOCASA_TENANT_ID);
   * ```
   */
  id: I;

  /**
   * The list of hosts that the tenant is available on.
   *
   * This is used to determine the tenant based on the hostname.
   *
   * @example
   * ```ts
   * const tenant = getTenantIdByHost('zoocasa.com');
   * ```
   */
  hosts: string[];

  contentHosts: ContentHosts;
  /**
   * The blog API URL for the tenant. This is used by the WordPress API to fetch blog posts from the tenant's blog.
   *
   * @example
   * ```ts
   * const tenant = getTenantById(ZOOCASA_TENANT_ID);
   * const blogApiUrl = tenant.blogApiUrl;
   * const ZoocasaBlogPostsApi: BlogPostsApiType = createWordPressApi<typeof EMBEDDED_FEATURED_MEDIA, PostsType<EmbeddedFeaturedMedia>>(blogApiUrl);
   * ```
   */
  blogApiUrl?: string;

  /**
   * The theme object for the tenant. This is only for styles and colors and we need to migrate to MUI theming.
   *
   * @example
   * ```ts
   * const tenant = getTenantById(ZOOCASA_TENANT_ID);
   * const colors = tenant.theme.colors;
   * ```
   */
  theme: T;
  features: Features;
  localization: {
    languages: (string | SiteLanguage)[]; // This is the languages that are supported by the tenant
    countries: (string | CountryCode)[]; // This is the country that the tenant can be rendered in
    namespaces: Namespaces[]; // This is the i18n namespaces required for the tenant. Files are located in public/locales
    defaultLanguage: SiteLanguage;
    defaultCountry: CountryCode;
  };
  config: C;
  routes: TenantRouteConfig;
  debug: {
    forceTenantHeaderValue: string;
    forceTenantFeatureValue: string;
  };
  googleTagManager: {
    containerIds: Record<G, string> & Record<typeof DEFAULT_GTM_CONTAINER_ID_KEY, string>;
  };
  sso: {
    googleWebClientId: string;
    facebookAppId: string;
  };
  sitemap: {
    url: string[];
  };
  pvr: {
    phoneNumber: string;
  };
}>;
